'use client';

import { useEffect, useRef, useState } from 'react';
import {
  ChartBarIcon,
  MegaphoneIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { Popover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import {
  statusBarIconButtonClassName,
  statusBarIconButtonProps,
} from './status-bar-icon-button';
import { classifyFeedbackEvent } from '@/shared/feedback-classify';
import { fetchFeedbackEvents } from '@/shared/cors-edge-lambda-api';
import { getGatewaySession } from '@/lib/gateway-fetch';

interface FeedbackPreviewEvent {
  event_id?: string;
  event_key?: string;
  id?: string;
  event_name?: string;
  feedback_value?: string;
  feedback?: string;
  user_email?: string;
  email?: string;
  user_id?: string;
  recorded_at?: string;
  timestamp?: string;
  created_at?: string;
  event_data?: { tabContext?: { report_type?: string }; [k: string]: unknown };
}

export type FeedbackAnalyticsPopoverProps = {
  /** Opens above the trigger when the status bar is docked to the bottom. */
  placement?: 'top-start' | 'bottom-end';
};

const FEEDBACK_SESSION_ERROR = 'session' as const;
const FEEDBACK_LOAD_ERROR = 'load' as const;

export function FeedbackAnalyticsPopover({
  placement = 'top-start',
}: FeedbackAnalyticsPopoverProps) {
  const t = useTimePilotT();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<FeedbackPreviewEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<'session' | 'load' | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      fetchedRef.current = false;
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setFetching(true);
    setError(null);

    let cancelled = false;
    const PREVIEW_PAGE_SIZE = 200;

    const load = async () => {
      try {
        // Same direct-CORS gateway path the Analytics view uses. This component
        // originally called /api/protected/feedback, a BFF route TR-1684 had
        // already deleted, so every count rendered 0 and swallowed the 404.
        const session = await getGatewaySession();
        if (!session) {
          if (!cancelled) setError(FEEDBACK_SESSION_ERROR);
          return;
        }
        const sessionArgs = { cellId: session.cellId, accessToken: session.accessToken };

        const firstData = await fetchFeedbackEvents({
          ...sessionArgs,
          page: 1,
          pageSize: PREVIEW_PAGE_SIZE,
        });
        const totalCount = firstData.totalFeedback ?? 0;
        let all = (firstData.feedbackEvents ?? []) as FeedbackPreviewEvent[];
        if (cancelled) return;
        setTotal(totalCount);
        setEvents(all);

        const totalPages = Math.ceil(totalCount / PREVIEW_PAGE_SIZE);
        for (let page = 2; page <= totalPages; page++) {
          if (cancelled) return;
          try {
            const data = await fetchFeedbackEvents({ ...sessionArgs, page, pageSize: PREVIEW_PAGE_SIZE });
            all = [...all, ...((data.feedbackEvents ?? []) as FeedbackPreviewEvent[])];
            if (!cancelled) setEvents(all);
          } catch {
            // Counts so far are still worth showing; stop walking pages.
            break;
          }
        }
      } catch (err) {
        // Never fail silently here: a swallowed error is indistinguishable from
        // "this tenant has no feedback", which is what hid the broken route.
        if (!cancelled) setError(FEEDBACK_LOAD_ERROR);
      } finally {
        if (!cancelled) setFetching(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const counts = { positive: 0, negative: 0, issue: 0, request: 0 };
  for (const e of events) {
    const k = classifyFeedbackEvent(e);
    if (k !== 'other') counts[k as keyof typeof counts]++;
  }
  const recent = [...events]
    .sort((a, b) =>
      (b.recorded_at ?? b.timestamp ?? b.created_at ?? '').localeCompare(
        a.recorded_at ?? a.timestamp ?? a.created_at ?? '',
      ),
    )
    .slice(0, 3);
  const userLabel = (e: FeedbackPreviewEvent) => e.user_email ?? e.email ?? e.user_id ?? '—';
  const fmtTime = (e: FeedbackPreviewEvent) => {
    const raw = e.recorded_at ?? e.timestamp ?? e.created_at;
    if (!raw) return '—';
    try {
      return new Date(raw).toLocaleDateString();
    } catch {
      return raw;
    }
  };

  return (
    <Popover
      id="feedback-analytics-popover"
      placement={placement}
      isOpen={open}
      onOpenChange={setOpen}
      maxWidth="21.25rem"
      contentClassName="rounded-xl"
      content={
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-4 gap-2">
            {[
              { id: 'good', label: t('feedback.good'), value: counts.positive, color: 'text-green-400', bg: 'bg-green-500/10', icon: <ThumbsUpIcon size={14} weight="fill" className="text-green-400" /> },
              { id: 'bad', label: t('feedback.bad'), value: counts.negative, color: 'text-red-400', bg: 'bg-red-500/10', icon: <ThumbsDownIcon size={14} weight="fill" className="text-red-400" /> },
              { id: 'issues', label: t('feedback.issues'), value: counts.issue, color: 'text-orange-400', bg: 'bg-orange-500/10', icon: <span className="text-orange-400 text-xs font-bold leading-none">!</span> },
              { id: 'req', label: t('feedback.req'), value: counts.request, color: 'text-blue-400', bg: 'bg-blue-500/10', icon: <MegaphoneIcon size={14} className="text-blue-400" /> },
            ].map(({ id, label, value, color, bg, icon }) => (
              <div key={id} className={`flex flex-col items-center gap-0.5 rounded-lg px-1 py-2 ${bg}`}>
                <div className="flex items-center gap-1">
                  {icon}
                  <span className="text-[10px] font-semibold uppercase text-gray-400">{label}</span>
                </div>
                {/* On error show '—', not 0: a confident zero reads as "no feedback exists". */}
                <span className={`text-xl font-bold ${color}`}>{fetching || error ? '—' : value}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--tp-popover-muted)]">
              {t('feedback.mostRecent')}
            </span>
            {fetching ? (
              <div className="py-2 text-center text-xs text-[var(--tp-popover-muted)]">{t('feedback.loading')}</div>
            ) : error ? (
              <div className="py-2 text-center text-xs text-red-400" role="alert">
                {error === FEEDBACK_SESSION_ERROR ? t('feedback.sessionExpired') : t('feedback.loadFailed')}
              </div>
            ) : recent.length === 0 ? (
              <div className="py-2 text-center text-xs text-[var(--tp-popover-muted)]">{t('feedback.noData')}</div>
            ) : (
              recent.map((e, i) => {
                const kind = classifyFeedbackEvent(e);
                return (
                  <div
                    key={String(e.event_key ?? e.event_id ?? e.id ?? i)}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white/10"
                  >
                    <span className="shrink-0">
                      {kind === 'positive' && <ThumbsUpIcon size={13} weight="fill" className="text-green-400" />}
                      {kind === 'negative' && <ThumbsDownIcon size={13} weight="fill" className="text-red-400" />}
                      {kind === 'issue' && <span className="text-[10px] font-bold text-orange-400">{t('feedback.issueTag')}</span>}
                      {kind === 'request' && <span className="text-[10px] font-bold text-blue-400">{t('feedback.reqTag')}</span>}
                    </span>
                    <span className="flex-1 truncate text-xs text-[var(--tp-popover-fg)]">{userLabel(e)}</span>
                    <span className="whitespace-nowrap text-[10px] text-[var(--tp-popover-muted)]">{fmtTime(e)}</span>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between border-t border-[var(--tp-popover-border)] pt-2">
            <span className="text-xs font-medium text-[var(--tp-popover-fg)]">
              {fetching ? '…' : error ? '—' : `${total} total`}
            </span>
            <a
              href="/analytics"
              className="flex items-center gap-1 text-xs font-semibold text-brand-blue-light no-underline transition-colors hover:text-brand-blue"
              onClick={() => setOpen(false)}
            >
              {t('feedback.openFull')}
            </a>
          </div>
        </div>
      }
    >
      {/* AGENT: do not restyle this trigger. Canonical circular inverse
          icon button — no className/prop overrides, no duotone. */}
      <Button
        id="AnalyticsStatusBarButton"
        type="button"
        {...statusBarIconButtonProps}
        aria-label={t('feedback.title')}
        className={statusBarIconButtonClassName}
        onClick={() => setOpen((current) => !current)}
      >
        <ChartBarIcon size={22} weight="regular" />
      </Button>
    </Popover>
  );
}
